import React, { useEffect, useState, useRef, useCallback } from "react";
import { Wand2, Loader2, Upload, Trash2, Image as ImageIcon, Sparkles, Gamepad2, Plus, Save, Pencil, Columns2, Drama } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Spinner } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { StylePicker } from "@/components/StylePicker";
import { DEFAULT_STYLE } from "@/lib/styles";
import { toast } from "sonner";

const KINDS = ["hero", "villain", "monster", "animal", "robot", "creature", "anime", "alien", "pet", "boss", "npc", "sidekick"];

// Visual-novel expression sprites — same character, different feelings/poses.
const EMOTIONS = [
  ["happy", "😄"], ["sad", "😢"], ["angry", "😠"], ["surprised", "😲"], ["thinking", "🤔"],
  ["neutral", "😐"], ["blush", "😊"], ["determined", "😤"], ["scared", "😨"], ["laughing", "😆"],
];
const DEFAULT_EMOTIONS = ["happy", "sad", "angry", "surprised", "thinking"];

// Body angles for staging a visual-novel cast.
const POSES = [["front", "🧍"], ["side", "🚶"], ["back", "🔙"], ["three_quarter", "↗️"]];
const POSE_LABELS = { front: "Front", side: "Side", back: "Back", three_quarter: "3/4" };
const DEFAULT_POSES = ["front", "side", "back", "three_quarter"];

function CharImage({ src, emoji, alt, imgClass, emojiClass, testid }) {
  const [err, setErr] = useState(false);
  useEffect(() => { setErr(false); }, [src]);
  if (!src || err) return <span className={emojiClass}>{emoji}</span>;
  return <img src={src} alt={alt} data-testid={testid} className={imgClass} onError={() => setErr(true)} />;
}

export default function CharacterStudio() {
  const [chars, setChars] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState("hero");
  const [style, setStyle] = useState(DEFAULT_STYLE);
  const [gen, setGen] = useState(false);
  const [file, setFile] = useState(null);
  const [upName, setUpName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [library, setLibrary] = useState([]);
  const [libCat, setLibCat] = useState(0);

  const load = () => api.get("/characters").then((r) => setChars(r.data));
  useEffect(() => {
    load();
    api.get("/characters/library").then((r) => setLibrary(r.data.categories || [])).catch(() => {});
  }, []);

  const applyArchetype = (a) => {
    setPrompt(`${a.name} — ${a.traits}`);
    setKind(KINDS.includes(a.kind) ? a.kind : "hero");
    toast.success(`Loaded "${a.name}" — tweak it and hit Generate!`);
  };

  const generate = async () => {
    if (prompt.trim().length < 2) return;
    setGen(true);
    try {
      const { data } = await api.post("/characters/generate", { prompt, kind, style });
      setChars((c) => [data, ...(c || [])]);
      setSelected(data);
      setPrompt("");
      toast.success(`Meet ${data.name}! 🎉`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setGen(false); }
  };

  const upload = async () => {
    if (!file) { toast.error("Choose an image first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("name", upName);
      fd.append("prompt", prompt);
      fd.append("kind", kind);
      const { data } = await api.post("/characters/upload", fd);
      setChars((c) => [data, ...(c || [])]);
      setSelected(data);
      setFile(null); setUpName(""); setPrompt("");
      toast.success(`${data.name} is ready to play! 🎨`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  if (chars === null) return <Spinner />;

  return (
    <div className="p-5 lg:p-8 max-w-6xl mx-auto" data-testid="character-studio">
      <PageHeader title="Character Studio" icon={Wand2} subtitle="Create original heroes, villains, monsters and more — then drop them into any game." />

      <Tabs defaultValue="ai" className="mb-8">
        <TabsList>
          <TabsTrigger value="ai" data-testid="char-tab-ai"><Sparkles className="w-4 h-4 mr-1.5" /> Describe with AI</TabsTrigger>
          <TabsTrigger value="upload" data-testid="char-tab-upload"><Upload className="w-4 h-4 mr-1.5" /> Upload & Remix</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="mt-4">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 space-y-3">
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} data-testid="char-prompt"
              placeholder="Describe any character… e.g. 'a brave fire-fox warrior with lightning powers' or 'a friendly slime robot sidekick'" rows={3} />
            <StylePicker value={style} onChange={setStyle} testid="char-style-picker" />
            <div className="flex flex-wrap items-center gap-3">
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger className="w-44" data-testid="char-kind"><SelectValue /></SelectTrigger>
                <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
              </Select>
              <Button onClick={generate} disabled={gen} data-testid="char-generate-btn"
                className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]">
                {gen ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : <><Wand2 className="w-4 h-4" /> Generate character</>}
              </Button>
            </div>
          </div>

          {library.length > 0 && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 mt-4" data-testid="char-library">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-[hsl(var(--primary))]" />
                <h3 className="font-bold text-sm">Inspiration library</h3>
                <span className="text-xs text-[hsl(var(--muted-foreground))]">— archetypes the AI knows. Tap one to start.</span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-2 mb-3">
                {library.map((cat, i) => (
                  <button key={cat.name} onClick={() => setLibCat(i)} data-testid={`lib-cat-${i}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors ${libCat === i ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]"}`}>{cat.name}</button>
                ))}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {(library[libCat]?.archetypes || []).map((a) => (
                  <button key={a.name} onClick={() => applyArchetype(a)} data-testid={`lib-arch-${a.name}`}
                    className="text-left rounded-xl border border-[hsl(var(--border))] p-3 hover:border-[hsl(var(--primary))]/60 hover:-translate-y-0.5 transition-all">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-7 h-7 rounded-lg shrink-0" style={{ background: `linear-gradient(135deg, ${a.color}, ${a.accent})` }} />
                      <span className="font-semibold text-sm truncate">{a.name}</span>
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] line-clamp-2">{a.traits}</div>
                    <Badge variant="secondary" className="mt-1.5 text-[9px] capitalize">{a.kind}</Badge>
                  </button>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="upload" className="mt-4">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5 space-y-3">
            <p className="text-sm text-[hsl(var(--muted-foreground))]">Upload a drawing, photo, sketch or AI image — we turn it into a playable cut-out character with AI-designed abilities. <b>Only upload art you own or have rights to.</b></p>
            <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="char-file"
              className="block text-sm file:mr-3 file:rounded-full file:border-0 file:bg-[hsl(var(--primary))] file:text-white file:px-4 file:py-2 file:font-semibold" />
            <Input value={upName} onChange={(e) => setUpName(e.target.value)} placeholder="Character name (optional)" data-testid="char-upname" />
            <Input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Describe its powers/abilities (optional)" data-testid="char-upprompt" />
            <Button onClick={upload} disabled={uploading || !file} data-testid="char-upload-btn" className="rounded-full">
              {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Building…</> : <><Upload className="w-4 h-4" /> Create from image</>}
            </Button>
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg font-bold">Your character library</h2>
        <span className="text-sm text-[hsl(var(--muted-foreground))]">{chars.length} saved</span>
      </div>
      {chars.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[hsl(var(--border))] p-12 text-center text-[hsl(var(--muted-foreground))]" data-testid="char-empty">
          <Wand2 className="w-8 h-8 mx-auto mb-2 opacity-50" />
          No characters yet — describe one above to get started!
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {chars.map((c) => (
            <button key={c.id} onClick={() => setSelected(c)} data-testid={`char-card-${c.id}`}
              className="text-left rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] overflow-hidden hover:border-[hsl(var(--primary))]/60 transition-all hover:-translate-y-1">
              <div className="aspect-square grid place-items-center bg-gradient-to-br from-[hsl(var(--secondary))] to-[hsl(var(--card))] relative">
                <CharImage src={c.image} emoji={c.emoji} alt={c.name}
                  imgClass="w-full h-full object-contain p-2" emojiClass="text-6xl" />
                <Badge className="absolute top-2 left-2 capitalize text-[10px]">{c.kind}</Badge>
              </div>
              <div className="p-3">
                <div className="font-bold truncate">{c.name}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{c.tagline || c.powers?.[0]}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      <CharacterDetail c={selected} onClose={() => setSelected(null)} onChange={load} />
    </div>
  );
}

function StatBar({ label, value }) {
  return (
    <div>
      <div className="flex justify-between text-xs mb-0.5"><span className="capitalize text-[hsl(var(--muted-foreground))]">{label}</span><span className="font-bold">{value}</span></div>
      <div className="h-2 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" style={{ width: `${value}%` }} />
      </div>
    </div>
  );
}

function CharacterDetail({ c, onClose, onChange }) {
  const [drawing, setDrawing] = useState(false);
  const [img, setImg] = useState(null);
  const [imgErr, setImgErr] = useState(false);
  const [games, setGames] = useState([]);
  const [gameId, setGameId] = useState("");
  const [target, setTarget] = useState("player");
  const [applying, setApplying] = useState(false);
  // 2.0 additions
  const [view, setView] = useState("preview"); // preview | portrait
  const [anim, setAnim] = useState("idle");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  // Multi-variant portrait generation
  const [variants, setVariants] = useState(c?.art_variants || []);
  const [vStyle, setVStyle] = useState(c?.style || DEFAULT_STYLE);
  const [vEngine, setVEngine] = useState("openai");
  const [genning, setGenning] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  // Visual-novel expression sprites
  const [sprites, setSprites] = useState(c?.sprites || {});
  const [emotions, setEmotions] = useState(DEFAULT_EMOTIONS);
  const [genSpritesBusy, setGenSpritesBusy] = useState(false);
  // Visual-novel body-angle poses
  const [poses, setPoses] = useState(c?.poses || {});
  const [selPoses, setSelPoses] = useState(DEFAULT_POSES);
  const [genPosesBusy, setGenPosesBusy] = useState(false);
  const [turntable, setTurntable] = useState(false);
  const [turnIdx, setTurnIdx] = useState(0);
  const [exportBusy, setExportBusy] = useState(null);
  const frameRef = useRef(null);

  useEffect(() => {
    setImg(c?.image || null); setImgErr(false); setGameId(""); setEditing(false); setAnim("idle");
    setVariants(c?.art_variants || []); setVStyle(c?.style || DEFAULT_STYLE); setCompareMode(false);
    setSprites(c?.sprites || {}); setEmotions(DEFAULT_EMOTIONS);
    setPoses(c?.poses || {}); setSelPoses(DEFAULT_POSES); setTurntable(false); setTurnIdx(0);
    setView(c?.image ? "preview" : "preview");
    if (c) {
      setForm({
        name: c.name || "", kind: c.kind || "hero", tagline: c.tagline || "",
        appearance: c.appearance || "", backstory: c.backstory || "", personality: c.personality || "",
        emoji: c.emoji || "✨", color: c.color || "#60A5FA", accent: c.accent || "#7CF7FF",
        powers: (c.powers || []).join(", "), abilities: (c.abilities || []).join(", "),
        stats: { ...(c.stats || {}) },
      });
      api.get("/games").then((r) => setGames(r.data.filter((g) => g.owner_id === c.owner_id))).catch(() => {});
    }
  }, [c]);

  const previewSrc = c ? `/character-preview.html?kind=${encodeURIComponent((form?.kind) || c.kind || "hero")}&col=${encodeURIComponent((form?.color || c.color || "#60A5FA").replace("#", "%23"))}&accent=${encodeURIComponent((form?.accent || c.accent || "#7CF7FF").replace("#", "%23"))}` : "";

  const postPreview = useCallback((patch) => {
    try { frameRef.current?.contentWindow?.postMessage({ type: "preview", ...patch }, "*"); } catch { /* ignore */ }
  }, []);

  useEffect(() => { postPreview({ anim }); }, [anim, postPreview]);
  useEffect(() => {
    const frames = DEFAULT_POSES.filter((p) => poses[p]);
    if (!turntable || frames.length < 2) return;
    const t = setInterval(() => setTurnIdx((i) => (i + 1) % frames.length), 900);
    return () => clearInterval(t);
  }, [turntable, poses]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (editing && form) postPreview({ kind: form.kind, col: form.color, accent: form.accent }); }, [editing, form?.kind, form?.color, form?.accent, postPreview]);

  if (!c || !form) return null;

  const setF = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setStat = (k, v) => setForm((f) => ({ ...f, stats: { ...f.stats, [k]: v } }));

  const draw = async () => {
    setDrawing(true);
    try {
      const { data } = await api.post(`/characters/${c.id}/art`);
      setImg(data.image); setImgErr(false); setView("portrait"); onChange();
      toast.success("Portrait drawn! 🎨");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setDrawing(false); }
  };

  const genVariants = async (refUrl = null) => {
    if (genning) return;
    setGenning(true);
    setView("portrait");
    try {
      const { data } = await api.post(`/characters/${c.id}/art/variants`,
        { style: vStyle, count: 3, engine: vEngine, ref_url: refUrl }, { timeout: 180000 });
      setVariants(data.variants || []);
      toast.success(`${(data.variants || []).length} versions ready — pick your favourite! ✨`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draw those."); }
    finally { setGenning(false); }
  };

  const selectVariant = async (v) => {
    setSelecting(v.id);
    try {
      const { data } = await api.post(`/characters/${c.id}/art/select`, { variant_id: v.id });
      setImg(data.image); setImgErr(false); onChange();
      toast.success("Portrait set! 🎨");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSelecting(null); }
  };

  const toggleEmotion = (e) =>
    setEmotions((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : cur.length >= 8 ? cur : [...cur, e]));

  const generateSprites = async () => {
    if (genSpritesBusy || !emotions.length) return;
    setGenSpritesBusy(true);
    try {
      const { data } = await api.post(`/characters/${c.id}/sprites`, { emotions }, { timeout: 240000 });
      setSprites(data.sprites || {});
      onChange();
      toast.success(`${emotions.length} expression${emotions.length === 1 ? "" : "s"} ready — drop them into your visual novel! 🎭`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draw those expressions."); }
    finally { setGenSpritesBusy(false); }
  };

  const togglePose = (p) =>
    setSelPoses((cur) => (cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p]));

  const generatePoses = async () => {
    if (genPosesBusy || !selPoses.length) return;
    setGenPosesBusy(true);
    try {
      const { data } = await api.post(`/characters/${c.id}/poses`, { poses: selPoses }, { timeout: 240000 });
      setPoses(data.poses || {});
      onChange();
      toast.success(`${selPoses.length} angle${selPoses.length === 1 ? "" : "s"} ready — stage your VN scenes! 🎬`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't draw those angles."); }
    finally { setGenPosesBusy(false); }
  };

  const poseFrames = DEFAULT_POSES.filter((p) => poses[p]).map((p) => [p, poses[p]]);

  const exportTurntable = async (fmt) => {
    if (exportBusy) return;
    setExportBusy(fmt);
    try {
      const { data } = await api.post(`/characters/${c.id}/turntable-export`, { format: fmt }, { timeout: 120000 });
      window.open(`${process.env.REACT_APP_BACKEND_URL}${data.url}`, "_blank");
      toast.success(`Turntable ${fmt === "sheet" ? "sprite-sheet" : "GIF"} ready — opened in a new tab! 🎉`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed."); }
    finally { setExportBusy(null); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, kind: form.kind, tagline: form.tagline, appearance: form.appearance,
        backstory: form.backstory, personality: form.personality, emoji: form.emoji,
        color: form.color, accent: form.accent,
        powers: form.powers.split(",").map((s) => s.trim()).filter(Boolean),
        abilities: form.abilities.split(",").map((s) => s.trim()).filter(Boolean),
        stats: form.stats,
      };
      await api.patch(`/characters/${c.id}`, payload);
      toast.success("Character updated! ✨");
      setEditing(false); onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const apply = async () => {
    if (applying) return;
    if (!gameId) { toast.error("Pick a game"); return; }
    setApplying(true);
    try {
      await api.post(`/characters/${c.id}/apply`, { game_id: gameId, target });
      toast.success(`Added ${c.name} to your game as ${target}!`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setApplying(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    try { await api.delete(`/characters/${c.id}`); onChange(); onClose(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const ANIMS = [["idle", "Idle"], ["walk", "Walk"], ["run", "Run"], ["jump", "Jump"]];

  return (
    <Dialog open={!!c} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="char-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><span className="text-2xl">{c.emoji}</span> {c.name}</DialogTitle>
          <DialogDescription className="capitalize">{c.kind} · {c.tagline}</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-5">
          <div>
            {/* Preview / portrait switch */}
            <div className="flex gap-1 mb-2 p-1 rounded-full bg-[hsl(var(--secondary))] w-fit">
              <button onClick={() => setView("preview")} data-testid="char-view-preview"
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${view === "preview" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>3D Preview</button>
              <button onClick={() => setView("portrait")} data-testid="char-view-portrait"
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${view === "portrait" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>Portrait</button>
            </div>

            {view === "preview" ? (
              <>
                <div className="aspect-square rounded-xl overflow-hidden bg-[#0b0d12] mb-2 border border-[hsl(var(--border))]">
                  <iframe ref={frameRef} title="character-preview" src={previewSrc} data-testid="char-anim-preview"
                    className="w-full h-full" style={{ border: 0 }} onLoad={() => { postPreview({ kind: form.kind, col: form.color, accent: form.accent, anim }); }} />
                </div>
                <div className="grid grid-cols-4 gap-1.5" data-testid="char-anim-controls">
                  {ANIMS.map(([key, label]) => (
                    <button key={key} onClick={() => setAnim(key)} data-testid={`char-anim-${key}`}
                      className={`py-1.5 rounded-lg text-xs font-semibold transition-colors ${anim === key ? "bg-[hsl(var(--primary))] text-white" : "bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--primary))]/20"}`}>{label}</button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="aspect-square rounded-xl grid place-items-center bg-gradient-to-br from-[hsl(var(--secondary))] to-[hsl(var(--card))] mb-3 overflow-hidden relative">
                  {genning && (
                    <div className="absolute inset-0 z-10 grid place-items-center bg-black/50 backdrop-blur-sm">
                      <div className="text-center text-white">
                        <Loader2 className="w-7 h-7 animate-spin mx-auto mb-2" />
                        <p className="text-xs">Drawing versions…</p>
                      </div>
                    </div>
                  )}
                  {img && !imgErr ? <img src={img} alt={c.name} onError={() => setImgErr(true)} className="w-full h-full object-contain p-2" data-testid="char-portrait" />
                    : <span className="text-7xl">{c.emoji}</span>}
                </div>

                {/* Style + engine controls */}
                <div className="mb-2">
                  <StylePicker value={vStyle} onChange={setVStyle} testid="char-variant-style" />
                </div>
                <div className="flex gap-1 mb-2 p-1 rounded-full bg-[hsl(var(--secondary))] w-fit" data-testid="char-engine-toggle">
                  <button onClick={() => setVEngine("openai")} data-testid="char-engine-openai"
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${vEngine === "openai" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>OpenAI</button>
                  <button onClick={() => setVEngine("gemini")} data-testid="char-engine-gemini"
                    className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${vEngine === "gemini" ? "bg-[hsl(var(--primary))] text-white" : "text-[hsl(var(--muted-foreground))]"}`}>Nano Banana</button>
                </div>
                <Button onClick={() => genVariants()} disabled={genning} data-testid="char-gen-variants-btn" className="w-full rounded-full bg-gradient-to-r from-[#1f6feb] to-[#a855f7]">
                  {genning ? <><Loader2 className="w-4 h-4 animate-spin" /> Drawing 3 versions…</> : <><Sparkles className="w-4 h-4" /> {variants.length ? "Generate new versions" : "Generate 3 versions"}</>}
                </Button>

                {/* Variant grid / compare */}
                {variants.length > 0 && (
                  <div className="mt-3" data-testid="char-variants">
                    {variants.length >= 2 && (
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-semibold text-[hsl(var(--muted-foreground))]">{variants.length} versions — pick your favourite</span>
                        <button onClick={() => setCompareMode((m) => !m)} data-testid="char-compare-toggle"
                          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[hsl(var(--primary))] hover:underline">
                          <Columns2 className="w-3.5 h-3.5" /> {compareMode ? "Grid view" : "Compare side-by-side"}
                        </button>
                      </div>
                    )}
                    {compareMode && variants.length >= 2 ? (
                      <div className="flex gap-2" data-testid="char-compare-view">
                        {variants.map((v, i) => {
                          const isSel = img === v.url;
                          return (
                            <div key={v.id} className={`flex-1 min-w-0 rounded-xl border-2 p-2 transition-colors ${isSel ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"}`} data-testid={`char-compare-${v.id}`}>
                              <div className="text-[10px] font-bold text-center mb-1">Version {i + 1}{isSel && " ✓"}</div>
                              <div className="aspect-square rounded-lg overflow-hidden bg-white mb-2"><img src={v.url} alt={`version ${i + 1}`} className="w-full h-full object-contain" /></div>
                              <Button size="sm" onClick={() => selectVariant(v)} disabled={selecting === v.id || isSel} data-testid={`char-compare-use-${v.id}`} className="w-full rounded-full text-[11px] h-7">
                                {selecting === v.id ? <Loader2 className="w-3 h-3 animate-spin" /> : isSel ? "Selected" : "Use this"}
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2">
                        {variants.map((v) => {
                          const isSel = img === v.url;
                          return (
                            <div key={v.id} className="relative group">
                              <button onClick={() => selectVariant(v)} data-testid={`char-variant-${v.id}`}
                                className={`aspect-square w-full rounded-lg overflow-hidden border-2 transition-all ${isSel ? "border-[hsl(var(--primary))] ring-2 ring-[hsl(var(--primary))]/40" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/60"}`}>
                                <img src={v.url} alt="variant" className="w-full h-full object-contain bg-white" />
                              </button>
                              {selecting === v.id && <div className="absolute inset-0 grid place-items-center bg-black/40 rounded-lg"><Loader2 className="w-4 h-4 animate-spin text-white" /></div>}
                              {isSel && <div className="absolute top-1 right-1 w-4 h-4 rounded-full bg-[hsl(var(--primary))] grid place-items-center text-white text-[9px]">✓</div>}
                              {vEngine === "gemini" && (
                                <button onClick={() => genVariants(v.url)} disabled={genning} data-testid={`char-more-like-${v.id}`}
                                  title="More like this" className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Sparkles className="w-3 h-3" />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <button onClick={draw} disabled={drawing} data-testid="char-draw-btn"
                  className="mt-2 text-[11px] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] font-medium disabled:opacity-50">
                  {drawing ? "Drawing…" : "or draw a single quick portrait"}
                </button>

                {/* Visual-novel expression sprites */}
                <div className="mt-4 pt-4 border-t border-[hsl(var(--border))]" data-testid="char-expressions">
                  <div className="flex items-center gap-1.5 mb-1">
                    <Drama className="w-4 h-4 text-[hsl(var(--primary))]" />
                    <h4 className="text-sm font-bold">Expression sprites</h4>
                    <Badge variant="secondary" className="text-[9px]">Visual novel</Badge>
                  </div>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">
                    Draw the same character showing different feelings — perfect for visual-novel scenes.
                    {!img && <span className="text-[hsl(var(--primary))]"> Draw a portrait first so every expression matches.</span>}
                  </p>
                  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="char-emotion-picker">
                    {EMOTIONS.map(([e, emo]) => {
                      const on = emotions.includes(e);
                      return (
                        <button key={e} onClick={() => toggleEmotion(e)} data-testid={`char-emotion-${e}`}
                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium capitalize transition-colors border ${on ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/60"}`}>
                          <span>{emo}</span> {e}
                        </button>
                      );
                    })}
                  </div>
                  <Button onClick={generateSprites} disabled={genSpritesBusy || !emotions.length} data-testid="char-gen-sprites-btn"
                    className="w-full rounded-full bg-gradient-to-r from-[#a855f7] to-[#ec4899]">
                    {genSpritesBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Drawing {emotions.length} expression{emotions.length === 1 ? "" : "s"}…</>
                      : <><Drama className="w-4 h-4" /> Generate {emotions.length} expression{emotions.length === 1 ? "" : "s"}</>}
                  </Button>
                  {Object.keys(sprites).length > 0 && (
                    <div className="grid grid-cols-3 gap-2 mt-3" data-testid="char-sprites-grid">
                      {Object.entries(sprites).map(([emo, url]) => (
                        <div key={emo} className="rounded-lg border border-[hsl(var(--border))] overflow-hidden" data-testid={`char-sprite-${emo}`}>
                          <div className="aspect-square bg-white"><img src={url} alt={emo} className="w-full h-full object-contain" /></div>
                          <div className="text-[10px] font-semibold text-center capitalize py-1 bg-[hsl(var(--secondary))]">{emo}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Body angles / poses */}
                  <div className="mt-4 pt-3 border-t border-dashed border-[hsl(var(--border))]" data-testid="char-poses">
                    <div className="flex items-center gap-1.5 mb-1">
                      <span className="text-sm">🎬</span>
                      <h4 className="text-sm font-bold">Poses &amp; angles</h4>
                    </div>
                    <p className="text-[11px] text-[hsl(var(--muted-foreground))] mb-2">Front, side, back &amp; 3/4 views of the same character for staging scenes.</p>
                    <div className="flex flex-wrap gap-1.5 mb-2" data-testid="char-pose-picker">
                      {POSES.map(([p, ic]) => {
                        const on = selPoses.includes(p);
                        return (
                          <button key={p} onClick={() => togglePose(p)} data-testid={`char-pose-opt-${p}`}
                            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors border ${on ? "bg-[hsl(var(--primary))] text-white border-[hsl(var(--primary))]" : "border-[hsl(var(--border))] hover:border-[hsl(var(--primary))]/60"}`}>
                            <span>{ic}</span> {POSE_LABELS[p]}
                          </button>
                        );
                      })}
                    </div>
                    <Button onClick={generatePoses} disabled={genPosesBusy || !selPoses.length} data-testid="char-gen-poses-btn"
                      className="w-full rounded-full bg-gradient-to-r from-[#0ea5e9] to-[#6366f1]">
                      {genPosesBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Drawing {selPoses.length} angle{selPoses.length === 1 ? "" : "s"}…</>
                        : <><span className="mr-1">🎬</span> Generate {selPoses.length} angle{selPoses.length === 1 ? "" : "s"}</>}
                    </Button>
                    {poseFrames.length >= 2 && (
                      <div className="mt-3 rounded-xl border border-[hsl(var(--border))] p-3 flex items-center gap-3" data-testid="char-turntable">
                        <div className="w-24 h-24 rounded-lg bg-white grid place-items-center overflow-hidden shrink-0">
                          <img src={poseFrames[turnIdx % poseFrames.length][1]} alt="turntable" className="w-full h-full object-contain" data-testid="char-turntable-frame" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-bold flex items-center gap-1.5">🎠 Turntable
                            <span className="text-[10px] font-normal text-[hsl(var(--muted-foreground))] capitalize">{POSE_LABELS[poseFrames[turnIdx % poseFrames.length][0]]}</span>
                          </div>
                          <p className="text-[10px] text-[hsl(var(--muted-foreground))] mb-2">Spin through every angle.</p>
                          <button onClick={() => setTurntable((t) => !t)} data-testid="char-turntable-btn"
                            className="inline-flex items-center gap-1 rounded-full bg-[hsl(var(--primary))] text-white text-[11px] font-semibold px-3 py-1 hover:opacity-90">
                            {turntable ? "⏸ Pause" : "▶ Play"}
                          </button>
                          <div className="flex gap-1.5 mt-2">
                            <button onClick={() => exportTurntable("gif")} disabled={!!exportBusy} data-testid="char-export-gif"
                              className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] text-[10px] font-semibold px-2 py-0.5 hover:border-[hsl(var(--primary))]/60 disabled:opacity-50">
                              {exportBusy === "gif" ? <Loader2 className="w-3 h-3 animate-spin" /> : "⬇ GIF"}
                            </button>
                            <button onClick={() => exportTurntable("sheet")} disabled={!!exportBusy} data-testid="char-export-sheet"
                              className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] text-[10px] font-semibold px-2 py-0.5 hover:border-[hsl(var(--primary))]/60 disabled:opacity-50">
                              {exportBusy === "sheet" ? <Loader2 className="w-3 h-3 animate-spin" /> : "⬇ Sprite sheet"}
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    {Object.keys(poses).length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mt-3" data-testid="char-poses-grid">
                        {Object.entries(poses).map(([angle, url]) => (
                          <div key={angle} className="rounded-lg border border-[hsl(var(--border))] overflow-hidden" data-testid={`char-pose-${angle}`}>
                            <div className="aspect-square bg-white"><img src={url} alt={angle} className="w-full h-full object-contain" /></div>
                            <div className="text-[10px] font-semibold text-center py-1 bg-[hsl(var(--secondary))]">{POSE_LABELS[angle] || angle}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* Stats: view or edit */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              {editing
                ? Object.entries(form.stats).map(([k, v]) => (
                  <div key={k}>
                    <div className="flex justify-between text-xs mb-0.5"><span className="capitalize text-[hsl(var(--muted-foreground))]">{k}</span><span className="font-bold">{v}</span></div>
                    <input type="range" min="0" max="100" value={v} onChange={(e) => setStat(k, Number(e.target.value))} data-testid={`char-stat-${k}`} className="w-full accent-[hsl(var(--primary))]" />
                  </div>
                ))
                : Object.entries(c.stats || {}).map(([k, v]) => <StatBar key={k} label={k} value={v} />)}
            </div>
          </div>

          {/* Right column: details or edit form */}
          {editing ? (
            <div className="space-y-2.5 text-sm" data-testid="char-edit-form">
              <Field label="Name"><Input value={form.name} onChange={(e) => setF("name", e.target.value)} data-testid="char-edit-name" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Type">
                  <Select value={form.kind} onValueChange={(v) => setF("kind", v)}>
                    <SelectTrigger data-testid="char-edit-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k} className="capitalize">{k}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Emoji"><Input value={form.emoji} onChange={(e) => setF("emoji", e.target.value)} data-testid="char-edit-emoji" /></Field>
              </div>
              <Field label="Tagline"><Input value={form.tagline} onChange={(e) => setF("tagline", e.target.value)} data-testid="char-edit-tagline" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Main color"><input type="color" value={form.color} onChange={(e) => setF("color", e.target.value)} data-testid="char-edit-color" className="w-full h-9 rounded-lg bg-transparent cursor-pointer" /></Field>
                <Field label="Accent / outfit"><input type="color" value={form.accent} onChange={(e) => setF("accent", e.target.value)} data-testid="char-edit-accent" className="w-full h-9 rounded-lg bg-transparent cursor-pointer" /></Field>
              </div>
              <Field label="Appearance / outfit"><Textarea rows={2} value={form.appearance} onChange={(e) => setF("appearance", e.target.value)} data-testid="char-edit-appearance" placeholder="e.g. silver armor with a glowing blue cape" /></Field>
              <Field label="Powers (comma separated)"><Input value={form.powers} onChange={(e) => setF("powers", e.target.value)} data-testid="char-edit-powers" /></Field>
              <Field label="Abilities (comma separated)"><Input value={form.abilities} onChange={(e) => setF("abilities", e.target.value)} data-testid="char-edit-abilities" /></Field>
              <Field label="Personality"><Textarea rows={2} value={form.personality} onChange={(e) => setF("personality", e.target.value)} data-testid="char-edit-personality" /></Field>
              <Field label="Backstory"><Textarea rows={3} value={form.backstory} onChange={(e) => setF("backstory", e.target.value)} data-testid="char-edit-backstory" /></Field>
            </div>
          ) : (
            <div className="space-y-3 text-sm">
              {c.backstory && <div><div className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase mb-0.5">Backstory</div><p>{c.backstory}</p></div>}
              {c.powers?.length > 0 && <div><div className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase mb-1">Powers</div><div className="flex flex-wrap gap-1.5">{c.powers.map((p, i) => <Badge key={i} variant="secondary">{p}</Badge>)}</div></div>}
              {c.abilities?.length > 0 && <div><div className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase mb-1">Abilities</div><div className="flex flex-wrap gap-1.5">{c.abilities.map((p, i) => <Badge key={i}>{p}</Badge>)}</div></div>}
              {c.signatureMove?.name && (
                <div data-testid="char-signature-move" className="rounded-xl border border-[hsl(var(--primary))]/40 bg-gradient-to-r from-[#a855f7]/10 to-[#ec4899]/10 p-2.5">
                  <div className="text-xs font-bold text-[hsl(var(--primary))] flex items-center gap-1.5">⚡ Signature Move: {c.signatureMove.name}</div>
                  {c.signatureMove.desc && <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5">{c.signatureMove.desc}</div>}
                </div>
              )}
              {c.personality && <div><div className="text-xs font-bold text-[hsl(var(--muted-foreground))] uppercase mb-0.5">Personality</div><p>{c.personality}</p></div>}
            </div>
          )}
        </div>

        {/* Edit / save controls */}
        <div className="flex items-center gap-2 mt-1">
          {editing ? (
            <>
              <Button onClick={save} disabled={saving} className="rounded-full bg-gradient-to-r from-[#1f6feb] to-[#3b9eff]" data-testid="char-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
              </Button>
              <Button variant="ghost" onClick={() => setEditing(false)} data-testid="char-cancel-edit">Cancel</Button>
            </>
          ) : (
            <Button variant="outline" onClick={() => setEditing(true)} className="rounded-full" data-testid="char-edit-btn"><Pencil className="w-4 h-4" /> Edit character</Button>
          )}
        </div>

        <div className="rounded-xl border border-[hsl(var(--border))] p-3 mt-2">
          <div className="text-sm font-bold mb-2 flex items-center gap-1.5"><Gamepad2 className="w-4 h-4" /> Use in a game</div>
          <div className="flex flex-wrap gap-2 items-center">
            <Select value={gameId} onValueChange={setGameId}>
              <SelectTrigger className="w-48" data-testid="char-game-select"><SelectValue placeholder="Pick a game" /></SelectTrigger>
              <SelectContent>{games.map((g) => <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger className="w-36" data-testid="char-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="player">Hero</SelectItem>
                <SelectItem value="enemy">Enemy</SelectItem>
                <SelectItem value="boss">Boss</SelectItem>
                <SelectItem value="npc">NPC</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={apply} disabled={applying} data-testid="char-apply-btn" className="rounded-full">
              {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to game
            </Button>
          </div>
        </div>

        <Button variant="ghost" onClick={del} data-testid="char-delete-btn" className="text-red-500 hover:text-red-600 mt-1 w-fit"><Trash2 className="w-4 h-4" /> Delete</Button>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-bold text-[hsl(var(--muted-foreground))] uppercase mb-1">{label}</div>
      {children}
    </div>
  );
}
